import React, { useMemo, useState } from "react";
import {
  FaArrowRight,
  FaCalendarAlt,
  FaCheck,
  FaClock,
  FaPaperPlane,
  FaPhoneAlt,
  FaTimes,
  FaUserTie,
} from "react-icons/fa";
import axios from "axios";
import { Fade } from "react-reveal";
import Footer from "../../components/NewView/Common/Footer";
import Header from "../../components/NewView/Common/Header";
import { baseUrl } from "../../store/slices/auth";
import { getLeadAttributionFields } from "../../utils/leadAttribution";

type Consultant = {
  id: string;
  name: string;
  role: string;
  image: string;
  imagePosition: string;
  accent: string;
  short: string;
  focus: string[];
};

type ConsultationForm = {
  consultantId: string;
  fullName: string;
  phone: string;
  preferredTime: string;
  question: string;
};

const allConsultants: Consultant[] = [
  {
    id: "shapovalova-olena",
    name: "Олена Шаповалова",
    role: "Кар'єрний консультант, ментор, психолог",
    image: "assets/Images/advisors/Shapovalova.jpg",
    imagePosition: "object-[center_18%]",
    accent: "Системний погляд на кар'єру",
    short:
      "Поєднує управлінський досвід у бізнесі, інженерну логіку та психологічні інструменти. Допомагає розібратися зі страхом змін, вигоранням і рухом у кар'єрі без зайвої води.",
    focus: ["кар'єрні зміни", "вигорання і стрес", "внутрішні опори", "системний план дій"],
  },
  // {
  //   id: "maryachkina-olena",
  //   name: "Олена Мар'ячкіна",
  //   role: "Психологиня, консультантка з першого кар'єрного кроку",
  //   image: "assets/Images/advisors/Olena.jpg",
  //   imagePosition: "object-[center_30%]",
  //   accent: "Підтримка на старті",
  //   short:
  //     "Має психологічну освіту та практичний досвід у рекрутингу. Добре розуміє хвилювання перед першою роботою, співбесідами та питаннями щодо резюме.",
  //   focus: ["перша робота", "резюме", "підготовка до співбесіди", "впевненість у власних силах"],
  // },
  {
    id: "haponenko-hanna",
    name: "Ганна Гапоненко",
    role: "Кар'єрна консультантка, HR та рекрутинг",
    image: "assets/Images/advisors/Gaponenko.jpg",
    imagePosition: "object-[center_18%]",
    accent: "Резюме, співбесіди, новий старт",
    short:
      "Має освіту в соціальній роботі та досвід у HR і рекрутингу. Допомагає скласти сильне резюме, підготуватися до співбесіди та впевнено пройти період змін.",
    focus: ["сильне резюме", "пошук роботи", "зміна професії", "план наступних кроків"],
  },
];

const hiddenConsultantIds = new Set(["shapovalova-olena"]);
const consultants = allConsultants.filter(
  (consultant) => !hiddenConsultantIds.has(consultant.id)
);

const inputClass =
  "h-14 w-full rounded-2xl border-2 border-black bg-white px-4 text-base font-bold text-black outline-none transition focus:bg-[#fff9d6] focus:shadow-[4px_4px_0_#ffd400]";

const labelClass = "mb-2 block text-sm font-black uppercase tracking-[0.08em] text-black";

const ContactComponent = () => {
  const [form, setForm] = useState<ConsultationForm>({
    consultantId: consultants[0].id,
    fullName: "",
    phone: "",
    preferredTime: "",
    question: "",
  });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const selectedConsultant = useMemo(
    () => consultants.find((item) => item.id === form.consultantId) || consultants[0],
    [form.consultantId]
  );

  const updateForm = (field: keyof ConsultationForm, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    setError("");
    setSuccess(false);
  };

  const selectConsultant = (id: string) => {
    updateForm("consultantId", id);
    const formElement = document.getElementById("consultation-form");
    formElement?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const validateForm = () => {
    const fullName = form.fullName.trim();
    const phone = form.phone.trim();
    const phoneDigits = phone.replace(/\D/g, "");

    if (!fullName || !phone) {
      return "Заповніть ім'я та номер телефону.";
    }

    if (phoneDigits.length < 9) {
      return "Введіть коректний номер телефону.";
    }

    return "";
  };

  const sendTelegramRequest = async () => {
    await axios.post(`${baseUrl}telegram/lead-request`, {
      title: "Нова заявка на консультацію",
      fields: {
        Консультант: selectedConsultant.name,
        Напрям: selectedConsultant.role,
        "Ім'я": form.fullName.trim(),
        Телефон: form.phone.trim(),
        "Зручний час": form.preferredTime.trim() || "Не вказано",
        Запит: form.question.trim() || "Не вказано",
        ...getLeadAttributionFields(),
      },
    });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSending(true);
    setError("");
    setSuccess(false);

    try {
      await sendTelegramRequest();
      setSuccess(true);
      setForm({
        consultantId: selectedConsultant.id,
        fullName: "",
        phone: "",
        preferredTime: "",
        question: "",
      });
    } catch (requestError: any) {
      setError(requestError?.message || "Не вдалося надіслати заявку. Спробуйте ще раз.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="bg-white text-black">
      <Header />

      <main>
        <section className="relative overflow-hidden border-b-2 border-black bg-white">
          <div className="pointer-events-none absolute right-0 top-0 hidden h-full w-[34%] bg-[#ffd400] lg:block" />
          <div className="pointer-events-none absolute right-14 top-14 hidden h-44 w-72 bg-[repeating-linear-gradient(135deg,rgba(0,0,0,0.18)_0_2px,transparent_2px_12px)] lg:block" />

          <div className="container relative mx-auto px-4 py-12 md:py-16 lg:py-20">
            <div className="grid grid-cols-12 gap-8 lg:items-end">
              <div className="col-span-12 lg:col-span-8">
                <Fade bottom>
                  <p className="text-sm font-black uppercase tracking-[0.22em] text-[#c9a000]">
                    Yellow Hub
                  </p>
                  <h1 className="mt-4 text-[44px] font-black uppercase leading-[0.92] tracking-[-0.055em] text-black sm:text-[64px] lg:text-[88px]">
                    Кар'єрні{" "}
                    <span className="text-[#ffd400] drop-shadow-[2px_2px_0_#000]">
                      консультації
                    </span>
                  </h1>
                  <p className="mt-6 max-w-[760px] text-[17px] font-semibold leading-8 text-[#4b5563] sm:text-[19px]">
                    Обери консультантку, залиш контакт - і ми допоможемо розібратися з резюме,
                    співбесідою, пошуком роботи або зміною професійного напряму.
                  </p>
                </Fade>
              </div>

              <div className="col-span-12 lg:col-span-4">
                <Fade bottom>
                  <div className="rounded-[28px] border-2 border-black bg-[#ffd400] p-6 shadow-[7px_7px_0_#000]">
                    <div className="flex items-center gap-3">
                      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-black text-[#ffd400]">
                        <FaCalendarAlt />
                      </span>
                      <div>
                        <p className="text-xs font-black uppercase tracking-[0.16em] text-black/60">
                          Формат
                        </p>
                        <p className="text-xl font-black uppercase leading-tight text-black">
                          Індивідуальна зустріч
                        </p>
                      </div>
                    </div>
                    <p className="mt-4 text-sm font-bold leading-6 text-black/72">
                      Після заявки команда Yellow Hub зв'яжеться з тобою та погодить зручний час.
                    </p>
                  </div>
                </Fade>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#f7f8fb] py-12 md:py-16">
          <div className="container mx-auto px-4">
            <Fade bottom>
              <div className="mb-8 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div>
                  <p className="text-sm font-black uppercase tracking-[0.2em] text-[#c9a000]">
                    Команда
                  </p>
                  <h2 className="mt-2 text-[36px] font-black uppercase leading-none tracking-[-0.05em] text-black sm:text-[52px]">
                    До кого можна записатися
                  </h2>
                </div>
                <p className="max-w-[460px] text-sm font-semibold leading-6 text-[#667085]">
                  У кожної консультантки свій досвід, але мета одна - допомогти тобі
                  впевненіше рухатися до роботи та розвитку.
                </p>
              </div>
            </Fade>

            <div className="flex flex-wrap justify-center gap-8">
              {consultants.map((consultant) => (
                <Fade bottom key={consultant.id}>
                  <article className="flex h-full w-full max-w-[520px] flex-col overflow-hidden rounded-[28px] border-2 border-black bg-white shadow-[7px_7px_0_#ffd400] md:w-[calc(50%-16px)]">
                    <div className="relative h-[340px] bg-[#fff6bf]">
                      <img
                        src={consultant.image}
                        alt={consultant.name}
                        className={`h-full w-full object-cover ${consultant.imagePosition}`}
                      />
                      <div className="absolute left-4 top-4 hidden max-w-[calc(100%-2rem)] rounded-full border-2 border-black bg-[#ffd400] px-4 py-2 text-xs font-black uppercase leading-tight shadow-[3px_3px_0_#000] sm:block">
                        {consultant.accent}
                      </div>
                    </div>

                    <div className="flex min-w-0 flex-1 flex-col p-5">
                      <div className="mb-4 w-fit max-w-full break-words rounded-full border-2 border-black bg-[#ffd400] px-3 py-2 text-[10px] font-black uppercase leading-tight shadow-[2px_2px_0_#000] sm:hidden">
                        {consultant.accent}
                      </div>
                      <p className="text-xs font-black uppercase tracking-[0.16em] text-[#c9a000]">
                        {consultant.role}
                      </p>
                      <h3 className="mt-2 text-[28px] font-black uppercase leading-none tracking-[-0.04em] text-black">
                        {consultant.name}
                      </h3>
                      <p className="mt-4 text-[15px] font-semibold leading-7 text-[#4b5563]">
                        {consultant.short}
                      </p>

                      <div className="mt-5 flex min-w-0 flex-wrap gap-2">
                        {consultant.focus.map((item) => (
                          <span
                            key={item}
                            className="max-w-full break-words rounded-full border border-black/10 bg-[#fff6bf] px-3 py-1.5 text-[11px] font-black uppercase leading-tight text-black"
                          >
                            {item}
                          </span>
                        ))}
                      </div>

                      <div className="mt-auto pt-6 pr-1 pb-1">
                        <button
                          type="button"
                          onClick={() => selectConsultant(consultant.id)}
                          className="inline-flex min-h-[50px] w-full items-center justify-center gap-2 rounded-2xl border-2 border-black bg-black px-5 text-sm font-black uppercase text-[#ffd400] shadow-[3px_3px_0_#ffd400] transition hover:-translate-y-0.5"
                        >
                          Записатися
                          <FaArrowRight />
                        </button>
                      </div>
                    </div>
                  </article>
                </Fade>
              ))}
            </div>
          </div>
        </section>

        <section id="consultation-form" className="bg-white py-12 md:py-16">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-12 gap-8 lg:items-start">
              <div className="col-span-12 lg:col-span-5">
                <Fade bottom>
                  <div className="sticky top-28 rounded-[28px] border-2 border-black bg-black p-6 text-white shadow-[8px_8px_0_#ffd400]">
                    <p className="text-sm font-black uppercase tracking-[0.18em] text-[#ffd400]">
                      Обрана консультантка
                    </p>
                    <div className="mt-5 flex items-center gap-4">
                      <img
                        src={selectedConsultant.image}
                        alt={selectedConsultant.name}
                        className={`h-24 w-24 rounded-[22px] border-2 border-[#ffd400] object-cover ${selectedConsultant.imagePosition}`}
                      />
                      <div>
                        <h3 className="text-2xl font-black uppercase leading-tight text-white">
                          {selectedConsultant.name}
                        </h3>
                        <p className="mt-2 text-sm font-bold leading-5 text-white/70">
                          {selectedConsultant.role}
                        </p>
                      </div>
                    </div>

                    <div className="mt-6 space-y-3">
                      <div className="flex items-center gap-3 rounded-2xl border border-white/15 bg-white/10 p-4">
                        <FaUserTie className="text-[#ffd400]" />
                        <span className="text-sm font-black uppercase tracking-[0.08em] text-white">
                          Персональна консультація
                        </span>
                      </div>
                      <div className="flex items-center gap-3 rounded-2xl border border-white/15 bg-white/10 p-4">
                        <FaClock className="text-[#ffd400]" />
                        <span className="text-sm font-black uppercase tracking-[0.08em] text-white">
                          Час узгодимо після заявки
                        </span>
                      </div>
                    </div>
                  </div>
                </Fade>
              </div>

              <div className="col-span-12 lg:col-span-7">
                <Fade bottom>
                  <div className="rounded-[28px] border-2 border-black bg-white p-6 shadow-[8px_8px_0_#ffd400] sm:p-8">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <p className="text-sm font-black uppercase tracking-[0.2em] text-[#c9a000]">
                          Реєстрація
                        </p>
                        <h2 className="mt-2 text-[34px] font-black uppercase leading-none tracking-[-0.05em] text-black sm:text-[46px]">
                          Запис на консультацію
                        </h2>
                      </div>
                      {success && (
                        <button
                          type="button"
                          onClick={() => setSuccess(false)}
                          className="flex h-11 w-11 items-center justify-center rounded-2xl border-2 border-black bg-white text-black shadow-[3px_3px_0_#ffd400]"
                          aria-label="Закрити повідомлення"
                        >
                          <FaTimes />
                        </button>
                      )}
                    </div>

                    {success ? (
                      <div className="mt-7 rounded-[22px] border-2 border-black bg-[#fff6bf] p-5 shadow-[5px_5px_0_#000]">
                        <div className="flex items-start gap-4">
                          <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-black text-[#ffd400]">
                            <FaCheck />
                          </span>
                          <div>
                            <h3 className="text-2xl font-black uppercase tracking-[-0.04em] text-black">
                              Заявку надіслано
                            </h3>
                            <p className="mt-2 text-sm font-semibold leading-6 text-[#4b5563]">
                              Ми отримали контакт і зв'яжемося з вами для узгодження часу консультації.
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <form className="mt-7 space-y-5" onSubmit={handleSubmit}>
                        <label className="block">
                          <span className={labelClass}>Консультантка</span>
                          <select
                            value={form.consultantId}
                            onChange={(event) => updateForm("consultantId", event.target.value)}
                            className={inputClass}
                          >
                            {consultants.map((consultant) => (
                              <option key={consultant.id} value={consultant.id}>
                                {consultant.name}
                              </option>
                            ))}
                          </select>
                        </label>

                        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                          <label className="block">
                            <span className={labelClass}>Ім'я та прізвище</span>
                            <input
                              value={form.fullName}
                              onChange={(event) => updateForm("fullName", event.target.value)}
                              className={inputClass}
                              autoComplete="name"
                            />
                          </label>

                          <label className="block">
                            <span className="mb-2 flex items-center gap-2 text-sm font-black uppercase tracking-[0.08em] text-black">
                              <FaPhoneAlt className="text-[#c9a000]" />
                              Номер телефону
                            </span>
                            <input
                              value={form.phone}
                              onChange={(event) => updateForm("phone", event.target.value)}
                              className={inputClass}
                              placeholder="+380..."
                              autoComplete="tel"
                              inputMode="tel"
                            />
                          </label>
                        </div>

                        <label className="block">
                          <span className={labelClass}>Зручний час для зв'язку</span>
                          <input
                            value={form.preferredTime}
                            onChange={(event) => updateForm("preferredTime", event.target.value)}
                            className={inputClass}
                          />
                        </label>

                        <label className="block">
                          <span className={labelClass}>Коротко про запит</span>
                          <textarea
                            value={form.question}
                            onChange={(event) => updateForm("question", event.target.value)}
                            rows={5}
                            className="w-full rounded-2xl border-2 border-black bg-white px-4 py-3 text-base font-bold text-black outline-none transition focus:bg-[#fff9d6] focus:shadow-[4px_4px_0_#ffd400]"
                          />
                        </label>

                        {error && (
                          <p className="rounded-2xl border-2 border-[#d92d20] bg-[#fff1f0] px-4 py-3 text-sm font-bold text-[#b42318]">
                            {error}
                          </p>
                        )}

                        <button
                          type="submit"
                          disabled={sending}
                          className="inline-flex min-h-[58px] w-full items-center justify-center gap-2 rounded-2xl border-2 border-black bg-[#ffd400] px-7 text-sm font-black uppercase text-black shadow-[5px_5px_0_#000] transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-70"
                        >
                          {sending ? "Надсилаємо..." : "Надіслати заявку"}
                          <FaPaperPlane />
                        </button>
                      </form>
                    )}
                  </div>
                </Fade>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </section>
  );
};

export default ContactComponent;
